import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useLanguageCurrency } from '../../context/LanguageCurrencyContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import api from '../../utils/api';
import { getSavedPropertiesFromAPI, removeSavedPropertyAPI } from '../../utils/savedProperties';

// Material-UI components
import { 
  Grid, 
  Box,
  Card,
  CardContent,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';

// Icons
import {
  Dashboard as DashboardIcon,
  Weekend as WeekendIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Favorite as FavoriteIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  MenuOpen as MenuOpenIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';

// Import booking data
import generateSampleBookings from '../../layouts-material/dashboard/data/bookingsData';

// Create HOSTA GLOBAL theme (dark theme matching website)
const hostaTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D5B577', // HOSTA GLOBAL gold
    },
    secondary: {
      main: '#1D2A3B', // HOSTA GLOBAL dark blue
    },
    background: {
      default: '#1D2A3B',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#9CA3AF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(213, 181, 119, 0.2)',
          borderRadius: '12px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1D2A3B',
          borderRight: '1px solid rgba(213, 181, 119, 0.2)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(29, 42, 59, 0.95)',
          border: '1px solid rgba(213, 181, 119, 0.2)',
        },
      },
    },
  },
});

// Safely get a string from API fields that may be { en, ar } or a plain string
const getI18nString = (val, fallback = '') => {
  if (val == null) return fallback;
  if (typeof val === 'object' && !Array.isArray(val) && (val.en != null || val.ar != null)) return val.en || val.ar || fallback;
  return String(val);
};

const ClientDashboard = () => {
  const { user, logout, getDashboardPath, updateUser } = useContext(AuthContext);
  const { formatPrice, language } = useLanguageCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardBookings, setDashboardBookings] = useState([]);
  const [bookingProperties, setBookingProperties] = useState([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // Initialize from URL hash so /client#bookings shows bookings on first paint
  const [activeView, setActiveView] = useState(() => {
    if (typeof window !== 'undefined') {
      const hash = (window.location.hash || '').replace('#', '').toLowerCase();
      if (['dashboard', 'bookings', 'saved', 'profile', 'settings'].includes(hash)) return hash;
    }
    return 'dashboard';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  
  // Profile form state
  const [profileFormData, setProfileFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    nationality: user?.nationality || 'Egyptian',
    phone: user?.phone || '',
    countryCode: user?.countryCode || '+20',
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [idDocument, setIdDocument] = useState(null);
  const [idDocumentPreview, setIdDocumentPreview] = useState(null);
  const [idUploadStatus, setIdUploadStatus] = useState('pending'); // 'pending', 'uploaded', 'approved', 'rejected'
  const [idUploading, setIdUploading] = useState(false);

  useEffect(() => {
    fetchData();
    const sampleBookings = generateSampleBookings();
    setDashboardBookings(sampleBookings);
    
    // Check hash immediately on mount
    const checkInitialHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['dashboard', 'bookings', 'saved', 'profile', 'settings'].includes(hash)) {
        setActiveView(hash);
      }
    };
    checkInitialHash();
  }, []);

  // Handle view from navigation state or hash (prefer window.location.hash for direct /client#bookings)
  useEffect(() => {
    // Check for view in location state (from profile menu navigation)
    if (location.state?.view && ['dashboard', 'bookings', 'saved', 'profile', 'settings'].includes(location.state.view)) {
      setActiveView(location.state.view);
      window.history.replaceState({}, '', location.pathname);
      return;
    }
    const hashFromWindow = (window.location.hash || '').replace('#', '').toLowerCase();
    const hashFromLocation = (location.hash || '').replace('#', '').toLowerCase();
    const hash = hashFromWindow || hashFromLocation;
    if (hash && ['dashboard', 'bookings', 'saved', 'profile', 'settings'].includes(hash)) {
      setActiveView(hash);
    } else if (location.pathname === getDashboardPath()) {
      setActiveView('dashboard');
    }
  }, [location.state, location.hash, location.pathname, getDashboardPath]);

  // Also listen for hashchange events (for browser back/forward and direct navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['dashboard', 'bookings', 'saved', 'profile', 'settings'].includes(hash)) {
        setActiveView(hash);
      } else {
        setActiveView('dashboard');
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/');
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (profileErrors[name]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear success message when user starts typing
    if (profileSuccess) {
      setProfileSuccess(false);
    }
  };

  // Update profile form data when user changes
  useEffect(() => {
    if (user) {
      setProfileFormData(prev => ({
        ...prev,
        nationality: user.nationality || 'Egyptian',
        phone: user.phone || '',
        countryCode: user.countryCode || '+20',
      }));
    }
  }, [user]);

  const handleProfileInfoSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileErrors({});
    setProfileSuccess(false);
    
    try {
      const updateData = {
        phone: profileFormData.phone,
        countryCode: profileFormData.countryCode,
      };
      
      await updateUser(user.id, updateData);
      setIsEditingProfile(false);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update profile';
      setProfileErrors({ submit: errorMessage });
    } finally {
      setProfileLoading(false);
    }
  };

  const validateProfileForm = () => {
    const errors = {};
    
    // Validate password change
    if (!profileFormData.currentPassword) {
      errors.currentPassword = 'Current password is required to change password';
    }
    if (!profileFormData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (profileFormData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (!profileFormData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (profileFormData.newPassword !== profileFormData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateProfileForm();
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }
    
    setProfileLoading(true);
    setProfileErrors({});
    setProfileSuccess(false);
    
    try {
      const updateData = {
        password: profileFormData.newPassword,
        currentPassword: profileFormData.currentPassword,
      };
      
      await updateUser(user.id, updateData);
      
      // Clear password fields on success
      setProfileFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Close the form
      setShowPasswordForm(false);
      
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update password';
      if (errorMessage.includes('Current password')) {
        setProfileErrors({ currentPassword: errorMessage });
      } else {
        setProfileErrors({ submit: errorMessage });
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleTogglePasswordForm = () => {
    setShowPasswordForm(!showPasswordForm);
    // Clear form and errors when closing
    if (showPasswordForm) {
      setProfileFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setProfileErrors({});
      setProfileSuccess(false);
    }
  };

  const handleDashboardClick = () => {
    setProfileMenuOpen(false);
    navigate(getDashboardPath());
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch client bookings from API
      const response = await api.get('/api/bookings');
      const clientBookings = response.data?.reservations ?? response.data ?? [];
      setBookings(Array.isArray(clientBookings) ? clientBookings : []);
      
      // Fetch properties to get full details for bookings
      let propertiesMap = {};
      try {
        const propertiesResponse = await api.get('/api/properties');
        const properties = Array.isArray(propertiesResponse.data) ? propertiesResponse.data : (propertiesResponse.data?.properties || []);
        propertiesMap = properties.reduce((acc, prop) => {
          const key = prop.id ?? prop._id;
          if (key != null) {
            acc[String(key)] = prop;
            acc[key] = prop;
          }
          return acc;
        }, {});
      } catch (propError) {
        console.log('Properties API not available, using mock data');
        // Use mock properties data
        const mockProperties = [
          {
            id: 1, code: 'HST-001', title: 'Luxury Modern Apartment', location: 'Cairo, Egypt', address: 'Downtown Cairo',
            price: 1200, image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
            type: 'Apartment', bedrooms: 2, bathrooms: 2, sqft: 1200, category: 'compound',
          },
          {
            id: 2, code: 'HST-002', title: 'Beachfront Villa', location: 'Alexandria, Egypt', address: 'Alexandria Beach',
            price: 2500, image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
            type: 'Villa', bedrooms: 4, bathrooms: 3, sqft: 2500, category: 'separate',
          },
          {
            id: 3, code: 'HST-003', title: 'Cozy Studio Apartment', location: 'Giza, Egypt', address: 'Pyramids Area',
            price: 1000, image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
            type: 'Studio', bedrooms: 1, bathrooms: 1, sqft: 600, category: 'compound',
          },
        ];
        propertiesMap = mockProperties.reduce((acc, prop) => {
          acc[prop.id] = prop;
          return acc;
        }, {});
      }
      
      // Normalize API fields: backend uses _id, checkInDate, checkOutDate, property (populated), pricing
      const calendarBookings = clientBookings.map(booking => {
        const checkIn = booking.checkInDate || booking.checkIn || booking.startDate;
        const checkOut = booking.checkOutDate || booking.checkOut || booking.endDate;
        const prop = booking.property;
        const propertyName = typeof prop?.title === 'object' ? (prop?.title?.en || prop?.title?.ar) : (prop?.title || booking.propertyName || 'Property');
        const propertyId = (prop?._id || prop?.id || booking.propertyId || booking.property)?.toString?.() || (prop?._id || prop?.id || booking.propertyId || booking.property);
        return {
          id: booking._id || booking.id,
          title: propertyName,
          start: new Date(checkIn),
          end: new Date(checkOut),
          resource: {
            propertyName,
            guestName: booking.guestName || user?.name || 'Guest',
            status: booking.status,
            total: booking.pricing?.totalAmount ?? booking.total ?? booking.finalTotal ?? 0,
            propertyId,
            reservationNumber: booking.reservationNumber,
            earlyCheckInHours: booking.earlyCheckInHours ?? 0,
            numberOfGuests: booking.numberOfGuests ?? 1,
          },
        };
      });
      setDashboardBookings(calendarBookings);
      
      // Store property details for bookings (normalize i18n {en,ar} to strings so React never receives objects)
      const bookingProps = calendarBookings
        .map(booking => {
          const propId = booking.resource?.propertyId;
          const prop = propId && propertiesMap[propId] ? propertiesMap[propId] : (clientBookings.find(b => (b._id || b.id) === booking.id)?.property || null);
          if (!prop && !propId) return null;
          const base = prop ? { ...prop, id: prop.id || prop._id } : { id: propId, title: booking.resource?.propertyName };
          return {
            ...base,
            title: getI18nString(base.title, 'Property'),
            address: getI18nString(base.address),
            location: getI18nString(base.location),
            code: getI18nString(base.code),
            category: getI18nString(base.category),
            bookingId: booking.id,
            checkIn: booking.start,
            checkOut: booking.end,
            status: booking.resource?.status,
            reservationNumber: booking.resource?.reservationNumber,
            total: booking.resource?.total,
            earlyCheckInHours: booking.resource?.earlyCheckInHours ?? 0,
            numberOfGuests: booking.resource?.numberOfGuests ?? 1,
          };
        })
        .filter(Boolean);
      setBookingProperties(bookingProps);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Use sample data as fallback
      const sampleBookings = generateSampleBookings();
      setDashboardBookings(sampleBookings);
      // Create mock booking properties from sample bookings
      const mockProperties = [
        {
          id: 1, code: 'HST-001', title: 'Luxury Modern Apartment', location: 'Cairo, Egypt', address: 'Downtown Cairo',
          price: 1200, image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
          type: 'Apartment', bedrooms: 2, bathrooms: 2, sqft: 1200, category: 'compound',
        },
        {
          id: 2, code: 'HST-002', title: 'Beachfront Villa', location: 'Alexandria, Egypt', address: 'Alexandria Beach',
          price: 2500, image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
          type: 'Villa', bedrooms: 4, bathrooms: 3, sqft: 2500, category: 'separate',
        },
      ];
      const mockBookingProps = sampleBookings.slice(0, 2).map((booking, idx) => ({
        ...mockProperties[idx % mockProperties.length],
        bookingId: booking.id,
        checkIn: booking.start,
        checkOut: booking.end,
        status: booking.resource?.status || 'confirmed',
      }));
      setBookingProperties(mockBookingProps);
    } finally {
      setLoading(false);
    }
  };


  // Menu items for sidebar
  const menuItems = [
    { name: 'Dashboard', icon: <DashboardIcon />, view: 'dashboard' },
    { name: 'My Bookings', icon: <WeekendIcon />, view: 'bookings' },
    { name: 'Saved Properties', icon: <StoreIcon />, view: 'saved' },
    { name: 'Profile', icon: <PersonIcon />, view: 'profile' },
    { name: 'Settings', icon: <SettingsIcon />, view: 'settings' },
  ];

  const handleMenuClick = (view) => {
    setActiveView(view);
    // Update URL hash to match the view
    window.location.hash = view;
  };

  // Calculate statistics from actual bookings (API: checkInDate, pricing.totalAmount, status e.g. Pending, Confirmed, CheckedOut)
  const totalBookings = bookings.length;
  const totalSpent = bookings.reduce((sum, booking) => sum + (booking.pricing?.totalAmount ?? booking.total ?? booking.finalTotal ?? 0), 0);
  const upcomingBookings = bookings.filter(b => {
    const checkIn = b.checkInDate ?? b.checkIn ?? b.startDate;
    return checkIn && new Date(checkIn) >= new Date();
  }).length;
  const completedBookings = bookings.filter(b =>
    ['CheckedOut', 'Checked out', 'completed', 'checked_out', 'Cancelled'].includes(String(b.status || ''))
  ).length;
  const [savedPropertiesList, setSavedPropertiesList] = useState([]);
  const [savedPropertiesLoading, setSavedPropertiesLoading] = useState(false);
  const [allProperties, setAllProperties] = useState([]);
  
  // Fetch saved properties from backend
  const fetchSavedProperties = () => {
    if (!user) {
      setSavedPropertiesList([]);
      return;
    }
    setSavedPropertiesLoading(true);
    getSavedPropertiesFromAPI()
      .then(({ properties }) => {
        const list = Array.isArray(properties) ? properties : [];
        setSavedPropertiesList(list.map((p) => ({ ...p, id: (p.id || p._id)?.toString?.() })));
      })
      .catch(() => setSavedPropertiesList([]))
      .finally(() => setSavedPropertiesLoading(false));
  };

  useEffect(() => {
    fetchSavedProperties();
  }, [user]);

  // Refresh saved properties when navigating to saved view
  useEffect(() => {
    if (activeView === 'saved' && user) {
      fetchSavedProperties();
    }
  }, [activeView, user]);

  const handleRemoveSaved = (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!propertyId) return;
    removeSavedPropertyAPI(propertyId).then(() => fetchSavedProperties()).catch(() => fetchSavedProperties());
  };

  const propertiesSaved = savedPropertiesList.length;

  // Statistics cards data
  const statCards = [
    {
      title: 'My Bookings',
      count: totalBookings,
      percentage: { amount: `${upcomingBookings} upcoming`, label: 'active bookings', color: 'success' },
      icon: <WeekendIcon />,
      color: 'dark',
      onClick: () => handleMenuClick('bookings'),
      clickable: true,
    },
    {
      title: 'Total Spent',
      count: totalSpent.toLocaleString(),
      percentage: { amount: '', label: 'across all bookings', color: 'success' },
      icon: <MoneyIcon />,
      color: 'info',
      clickable: false,
    },
    {
      title: 'Properties Saved',
      count: propertiesSaved,
      percentage: { amount: '', label: 'your favorites', color: 'success' },
      icon: <FavoriteIcon />,
      color: 'success',
      onClick: () => handleMenuClick('saved'),
      clickable: true,
    },
  ];

  if (loading) {
  return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
    }


  return (
    <ThemeProvider theme={hostaTheme}>
      <CssBaseline />
      <div style={{ minHeight: '100vh', backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <WhatsAppButton />
        
        <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#1D2A3B', pt: '64px' }}>
          {/* Icon Menu Bar - Top of Page */}
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(213, 181, 119, 0.2)',
              px: { xs: 1, sm: 2, md: 3 },
              py: { xs: 1, sm: 1.5 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 1, sm: 2, md: 3 },
              position: 'sticky',
              top: '64px',
              zIndex: 100,
              backdropFilter: 'blur(10px)',
            }}
          >
            {menuItems.map((item) => (
              <Box
                key={item.name}
                component="button"
                onClick={() => handleMenuClick(item.view)}
                title={item.name}
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  border: 'none',
                  background: activeView === item.view 
                    ? 'rgba(213, 181, 119, 0.2)' 
                    : 'transparent',
                  cursor: 'pointer',
                  color: activeView === item.view ? '#D5B577' : '#9CA3AF',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '40px', sm: '48px' },
                  minHeight: { xs: '40px', sm: '48px' },
                  transition: 'all 0.3s ease',
                  border: activeView === item.view 
                    ? '1px solid rgba(213, 181, 119, 0.4)' 
                    : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(213, 181, 119, 0.15)',
                    color: '#D5B577',
                    borderColor: 'rgba(213, 181, 119, 0.3)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {item.icon}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              width: '100%',
              p: { xs: 1, sm: 1.5, md: 3 },
              pt: { xs: 2, sm: 2.5, md: 3 },
              backgroundColor: '#1D2A3B',
            }}
          >

            {/* Dashboard Content */}
            <Box sx={{ 
              flex: 1, 
              minWidth: 0,
              pt: { xs: 0, md: 0 }, // No top padding at all
              mt: { xs: 0, md: 0 }, // No top margin at all
            }}>
              {/* Dashboard View */}
              {activeView === 'dashboard' && (
                <>
                  {/* Welcome Section */}
                  <Box 
                    sx={{ 
                      mb: { xs: 3, md: 5 },
                      p: { xs: 2, sm: 3, md: 5 },
                      borderRadius: { xs: 2, md: 4 },
                      mt: { xs: 0, md: 0 },
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.2) 0%, rgba(213, 181, 119, 0.1) 50%, rgba(213, 181, 119, 0.05) 100%)',
                      border: '2px solid rgba(213, 181, 119, 0.4)',
                      boxShadow: '0 8px 32px rgba(213, 181, 119, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: 'linear-gradient(90deg, #D5B577 0%, rgba(213, 181, 119, 0.8) 50%, #D5B577 100%)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(213, 181, 119, 0.15) 0%, transparent 70%)',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 2, md: 3 }, 
                      position: 'relative', 
                      zIndex: 1,
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <Box
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.3) 0%, rgba(213, 181, 119, 0.2) 100%)',
                          color: '#D5B577',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 24px rgba(213, 181, 119, 0.3)',
                          border: '2px solid rgba(213, 181, 119, 0.5)',
                          minWidth: { xs: '60px', md: '80px' },
                          minHeight: { xs: '60px', md: '80px' },
                        }}
                      >
                        <PersonIcon sx={{ fontSize: { xs: 32, md: 48 } }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#D5B577', 
                            mb: 1.5,
                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                            textShadow: '0 2px 8px rgba(213, 181, 119, 0.3)',
                            lineHeight: 1.2,
                          }}
                        >
                          Welcome back, {user?.name || 'Guest'}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#ffffff', 
                            fontSize: { xs: '1rem', md: '1.2rem' },
                            fontWeight: '500',
                            opacity: 0.9,
                            lineHeight: 1.6,
                          }}
                        >
                          Here's an overview of your bookings and activity
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

          {/* Statistics Cards - Desktop View */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, md: 4 }, display: { xs: 'none', sm: 'flex' }, width: '100%' }}>
            {statCards.map((card, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={index} 
                sx={{ 
                  display: 'flex',
                  width: '100%',
                  minWidth: 0,
                  flexBasis: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 16px)' },
                  maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 16px)' },
                }}
              >
                <Card
                  onClick={card.clickable && card.onClick ? card.onClick : undefined}
                  sx={{
                    width: '100%',
                    height: { xs: '160px', sm: '180px', md: '200px' },
                    minHeight: { xs: '160px', sm: '180px', md: '200px' },
                    maxHeight: { xs: '160px', sm: '180px', md: '200px' },
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.12) 0%, rgba(213, 181, 119, 0.06) 50%, rgba(29, 42, 59, 0.8) 100%)',
                    border: '1px solid rgba(213, 181, 119, 0.25)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: card.clickable ? 'pointer' : 'default',
                    boxSizing: 'border-box',
                    '&:hover': {
                      transform: card.clickable ? 'translateY(-4px) scale(1.02)' : 'none',
                      boxShadow: card.clickable ? '0 8px 32px rgba(213, 181, 119, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)' : '0 4px 20px rgba(0, 0, 0, 0.3)',
                      borderColor: card.clickable ? 'rgba(213, 181, 119, 0.5)' : 'rgba(213, 181, 119, 0.25)',
                      background: card.clickable ? 'linear-gradient(135deg, rgba(213, 181, 119, 0.18) 0%, rgba(213, 181, 119, 0.1) 50%, rgba(29, 42, 59, 0.85) 100%)' : 'linear-gradient(135deg, rgba(213, 181, 119, 0.12) 0%, rgba(213, 181, 119, 0.06) 50%, rgba(29, 42, 59, 0.8) 100%)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, transparent 0%, #D5B577 20%, #D5B577 80%, transparent 100%)',
                      opacity: 0.8,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: '150px',
                      height: '150px',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(213, 181, 119, 0.1) 0%, transparent 70%)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <CardContent 
                    sx={{ 
                      flex: '1 1 auto',
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      width: '100%',
                      p: { xs: 2.5, sm: 3, md: 3.5 },
                      position: 'relative',
                      zIndex: 1,
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      '&:last-child': {
                        pb: { xs: 2.5, sm: 3, md: 3.5 },
                      },
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: { xs: 2, sm: 2.5, md: 3 },
                      mb: { xs: 2, md: 2.5 },
                      flexShrink: 0,
                    }}>
                      <Box
                        sx={{
                          width: { xs: '48px', sm: '52px', md: '56px' },
                          height: { xs: '48px', sm: '52px', md: '56px' },
                          minWidth: { xs: '48px', sm: '52px', md: '56px' },
                          minHeight: { xs: '48px', sm: '52px', md: '56px' },
                          maxWidth: { xs: '48px', sm: '52px', md: '56px' },
                          maxHeight: { xs: '48px', sm: '52px', md: '56px' },
                          p: 0,
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                          color: '#D5B577',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 16px rgba(213, 181, 119, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(213, 181, 119, 0.3)',
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {card.icon}
                        </Box>
                      </Box>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: '700', 
                          color: '#ffffff', 
                          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                          lineHeight: 1.2,
                          letterSpacing: '-0.02em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {card.count}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      flex: '0 0 auto',
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'flex-end',
                      minHeight: 0,
                    }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#D5B577', 
                          fontWeight: '600',
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          letterSpacing: '0.01em',
                          textTransform: 'uppercase',
                          opacity: 0.9,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {card.title}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Statistics Icons - Mobile View */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            justifyContent: 'space-around',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            px: 1,
            flexWrap: 'wrap',
          }}>
            {statCards.map((card, index) => (
              <Box
                key={index}
                onClick={card.clickable && card.onClick ? card.onClick : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  p: 1,
                  cursor: card.clickable ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  flex: '1 1 auto',
                  minWidth: 0,
                  justifyContent: 'center',
                  '&:hover': {
                    opacity: card.clickable ? 0.8 : 1,
                  },
                }}
              >
                <Box
                  sx={{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    minHeight: '28px',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                    color: '#D5B577',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {card.icon}
                  </Box>
                </Box>
                <Typography 
                  sx={{ 
                    fontWeight: '700', 
                    color: '#ffffff', 
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {card.count}
                </Typography>
              </Box>
            ))}
          </Box>

                  {/* Quick Actions */}
                  <Grid container spacing={3} sx={{ display: 'flex', width: '100%' }}>
                    <Grid item xs={12} sx={{ 
                      display: 'flex',
                      width: '100%',
                      minWidth: 0,
                    }}>
                      <Card 
                        sx={{ 
                          width: '100%',
                          height: { xs: 'auto', sm: '300px', md: '320px' },
                          minHeight: { xs: 'auto', sm: '300px', md: '320px' },
                          maxHeight: { xs: 'none', sm: '300px', md: '320px' },
                          border: '1px solid rgba(213, 181, 119, 0.3)', 
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                          borderRadius: 3,
                          display: 'flex',
                          flexDirection: 'column',
                          flex: '1 1 auto',
                          flexShrink: 0,
                        }}
                      >
                        <CardContent sx={{ 
                          p: { xs: 2, sm: 3, md: 3.5 },
                          flex: '1 1 auto',
                          display: 'flex',
                          flexDirection: 'column',
                          height: { xs: 'auto', sm: '100%', md: '100%' },
                          width: '100%',
                          boxSizing: 'border-box',
                          overflow: { xs: 'visible', sm: 'hidden', md: 'hidden' },
                          '&:last-child': {
                            pb: { xs: 2, sm: 3, md: 3.5 },
                          },
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 'bold', 
                            color: '#D5B577', 
                            mb: { xs: 1.5, md: 2.5 },
                            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                            flexShrink: 0,
                          }}>
                            Quick Actions
                          </Typography>
                          <Grid container spacing={2} sx={{ display: 'flex', width: '100%', flex: { xs: '0 0 auto', sm: '1 1 auto' }, minHeight: 0 }}>
                            <Grid item xs={12} sm={6} sx={{ 
                              display: 'flex',
                              width: '100%',
                              minWidth: 0,
                              flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' },
                              maxWidth: { xs: '100%', sm: 'calc(50% - 8px)' },
                            }}>
                              <Box
                                component={Link}
                                to="/properties"
                                sx={{
                                  p: 3,
                                  width: '100%',
                                  height: { xs: '140px', sm: '160px', md: '160px' },
                                  minHeight: { xs: '140px', sm: '160px', md: '160px' },
                                  maxHeight: { xs: '140px', sm: '160px', md: '160px' },
                                  flex: '1 1 auto',
                                  flexShrink: 0,
                                  background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.08) 100%)',
                                  borderRadius: 2.5,
                                  border: '1px solid rgba(213, 181, 119, 0.4)',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(213, 181, 119, 0.25)',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 20px rgba(213, 181, 119, 0.3)',
                                    borderColor: 'rgba(213, 181, 119, 0.6)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(213, 181, 119, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <StoreIcon sx={{ color: '#D5B577', fontSize: 36 }} />
                                </Box>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ffffff', mb: 0.5 }}>
                                    Browse Properties
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                                    Find your next stay
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ 
                              display: 'flex',
                              width: '100%',
                              minWidth: 0,
                              flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' },
                              maxWidth: { xs: '100%', sm: 'calc(50% - 8px)' },
                            }}>
                              <Box
                                onClick={() => handleMenuClick('bookings')}
                                sx={{
                                  p: 3,
                                  width: '100%',
                                  height: { xs: '140px', sm: '160px', md: '160px' },
                                  minHeight: { xs: '140px', sm: '160px', md: '160px' },
                                  maxHeight: { xs: '140px', sm: '160px', md: '160px' },
                                  flex: '1 1 auto',
                                  flexShrink: 0,
                                  background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.08) 100%)',
                                  borderRadius: 2.5,
                                  border: '1px solid rgba(213, 181, 119, 0.4)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(213, 181, 119, 0.25)',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 20px rgba(213, 181, 119, 0.3)',
                                    borderColor: 'rgba(213, 181, 119, 0.6)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(213, 181, 119, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <WeekendIcon sx={{ color: '#D5B577', fontSize: 36 }} />
                                </Box>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ffffff', mb: 0.5 }}>
                                    My Bookings
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                                    View all bookings
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* My Bookings View */}
              {activeView === 'bookings' && (
                <Box sx={{ width: '100%', py: { xs: 2, md: 4 }, px: { xs: 4, sm: 6, md: 8 } }}>
                  <Box sx={{ maxWidth: '1280px', mx: 'auto', width: '100%' }}>
                    {/* Enhanced Header */}
                    <Box 
                      sx={{ 
                        mb: { xs: 2, md: 4 },
                        p: { xs: 1.5, sm: 2, md: 3 },
                        borderRadius: { xs: 2, md: 3 },
                        background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.08) 50%, rgba(213, 181, 119, 0.05) 100%)',
                        border: '1px solid rgba(213, 181, 119, 0.3)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, #D5B577 0%, rgba(213, 181, 119, 0.5) 100%)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexDirection: { xs: 'row', sm: 'row' }, textAlign: 'left' }}>
                        <Box
                          sx={{
                            p: { xs: 1, md: 2 },
                            borderRadius: { xs: 1.5, md: 2 },
                            background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.3) 0%, rgba(213, 181, 119, 0.15) 100%)',
                            color: '#D5B577',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(213, 181, 119, 0.2)',
                            flexShrink: 0,
                          }}
                        >
                          <WeekendIcon sx={{ fontSize: { xs: 20, md: 32 } }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, lineHeight: 1.2 }}>
                            My Bookings
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#9CA3AF', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, mt: { xs: 0.25, md: 0.5 }, display: { xs: 'none', sm: 'block' } }}>
                            Manage and view all your property bookings
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {bookingProperties.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        {bookingProperties.map((property) => (
                          <div
                            key={property.bookingId || property.id}
                            className="rounded-2xl overflow-hidden transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                            style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(213, 181, 119, 0.2)',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.4)';
                              e.currentTarget.style.boxShadow = '0 12px 40px rgba(213, 181, 119, 0.2), 0 0 0 1px rgba(213, 181, 119, 0.3)';
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.1)';
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                            }}
                            onClick={() => property.bookingId && navigate(`/client/booking/${property.bookingId}`)}
                          >
                            <div
                              style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', width: '100%', height: '100%', cursor: 'pointer' }}
                            >
                              <div className="relative h-56 overflow-hidden">
                                <img
                                  src={property.image || (property.images && property.images[0]) || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80'}
                                  alt={getI18nString(property.title)}
                                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                  loading="lazy"
                                />
                                {property.featured && (
                                  <span 
                                    className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
                                    style={{ 
                                      background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                                      color: '#1D2A3B',
                                      border: '1px solid rgba(213, 181, 119, 0.5)'
                                    }}
                                  >
                                    FEATURED
                                  </span>
                                )}
                                <span 
                                  className="absolute bottom-3 left-3 text-sm font-semibold px-4 py-2 rounded-lg shadow-xl"
                                  style={{ 
                                    background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.95) 0%, rgba(29, 42, 59, 0.85) 100%)',
                                    color: '#D5B577',
                                    border: '1px solid rgba(213, 181, 119, 0.3)',
                                    backdropFilter: 'blur(10px)'
                                  }}
                                >
                                  {formatPrice(property.price ?? 0, false)} / night
                                </span>
                                {property.status && (
                                  <span
                                    className="absolute top-3 right-3 text-xs font-bold px-2 py-1 rounded shadow-lg capitalize"
                                    style={{
                                      backgroundColor: (property.status || '').toLowerCase() === 'confirmed'
                                        ? 'rgba(76, 175, 80, 0.95)'
                                        : (property.status || '').toLowerCase() === 'pending'
                                        ? 'rgba(255, 152, 0, 0.95)'
                                        : (property.status || '').toLowerCase() === 'checkedout' || (property.status || '').toLowerCase() === 'cancelled'
                                        ? 'rgba(158, 158, 158, 0.95)'
                                        : 'rgba(158, 158, 158, 0.95)',
                                      color: '#fff',
                                      backdropFilter: 'blur(10px)'
                                    }}
                                  >
                                    {property.status}
                                  </span>
                                )}
                              </div>
                              <div className="p-5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                {property.reservationNumber && (
                                  <p className="text-xs mb-1 font-mono" style={{ color: '#D5B577' }}>
                                    {property.reservationNumber}
                                  </p>
                                )}
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                  <h3 className="text-lg font-bold text-white flex-1 min-w-0">
                                    {getI18nString(property.title, 'Property')}
                                  </h3>
                                  <div className="flex items-center gap-2">
                                    {getI18nString(property.code) && (
                                      <span className="text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                                        {getI18nString(property.code)}
                                      </span>
                                    )}
                                    {getI18nString(property.category) && (
                                      <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.3)' }}>
                                        {getI18nString(property.category) === 'compound' ? 'In Compound' : 'Separate'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-sm mb-4 flex items-center gap-1" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {getI18nString(property.address) || getI18nString(property.location)}
                                </p>
                                <div className="flex items-center gap-4 mb-4 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                  <div className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                    {property.bedrooms || property.beds} Beds
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                    </svg>
                                    {property.bathrooms || property.baths} Baths
                                  </div>
                                  {property.sqft && (
                                    <div className="flex items-center gap-1">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                      </svg>
                                      {Math.round(property.sqft * 0.092903)} m²
                                    </div>
                                  )}
                                </div>
                                {(property.checkIn || property.checkOut) && (
                                  <div className="mt-auto pt-2 border-t space-y-1" style={{ borderColor: 'rgba(213, 181, 119, 0.2)', flexShrink: 0 }}>
                                    {property.reservationNumber && (
                                      <p className="text-xs font-medium" style={{ color: '#D5B577' }}>
                                        {language === 'ar' ? 'الحجز' : 'Booking'} #{property.reservationNumber}
                                      </p>
                                    )}
                                    {property.checkIn && (
                                      <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        {language === 'ar' ? 'الدخول' : 'Check-in'}: {new Date(property.checkIn).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {property.earlyCheckInHours > 0
                                          ? (() => {
                                              const hour = 14 - property.earlyCheckInHours;
                                              const timeStr = new Date(2000, 0, 1, Math.max(0, hour), 0, 0).toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                              return ` • ${language === 'ar' ? 'من' : 'from'} ${timeStr}`;
                                            })()
                                          : ` • ${language === 'ar' ? 'من 2:00 م' : 'from 2:00 PM'}`}
                                      </p>
                                    )}
                                    {property.checkOut && (
                                      <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        {language === 'ar' ? 'الخروج' : 'Check-out'}: {new Date(property.checkOut).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {` • ${language === 'ar' ? 'حتى 12:00 م' : 'by 12:00 PM'}`}
                                      </p>
                                    )}
                                    {(property.numberOfGuests > 0 || property.total != null) && (
                                      <p className="text-xs flex justify-between items-center gap-2" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        {property.numberOfGuests > 0 && (
                                          <span>{property.numberOfGuests} {language === 'ar' ? 'نزيل' : 'guest'}{property.numberOfGuests !== 1 ? 's' : ''}</span>
                                        )}
                                        {property.total != null && property.total > 0 && (
                                          <span className="font-semibold" style={{ color: '#D5B577' }}>{formatPrice(property.total, false)}</span>
                                        )}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : dashboardBookings.length > 0 ? (
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                          {dashboardBookings.map((booking, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={booking.id || idx}>
                              <Box
                                sx={{
                                  p: { xs: 2, md: 3 },
                                  minHeight: { xs: 180, md: 220 },
                                  background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.12) 0%, rgba(213, 181, 119, 0.06) 100%)',
                                  borderRadius: { xs: 2, md: 2.5 },
                                  border: '1px solid rgba(213, 181, 119, 0.3)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(213, 181, 119, 0.18)',
                                    borderColor: 'rgba(213, 181, 119, 0.6)',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 20px rgba(213, 181, 119, 0.3)',
                                  },
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#D5B577', mb: { xs: 0.75, md: 1 }, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
                                  {getI18nString(booking.resource?.propertyName) || getI18nString(booking.title) || 'Property'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9CA3AF', mb: { xs: 0.75, md: 1 }, fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                  {booking.resource?.guestName || user?.name || 'Guest'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mb: { xs: 0.75, md: 1 }, flex: 1, fontSize: { xs: '0.75rem', md: '0.8125rem' } }}>
                                  {new Date(booking.start).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })} - {new Date(booking.end).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </Typography>
                                {booking.resource?.status && (
                                  <Box
                                    sx={{
                                      display: 'inline-block',
                                      px: { xs: 1, md: 1.25 },
                                      py: { xs: 0.5, md: 0.75 },
                                      borderRadius: { xs: 1, md: 1.5 },
                                      backgroundColor: booking.resource.status === 'confirmed' 
                                        ? 'rgba(76, 175, 80, 0.2)' 
                                        : booking.resource.status === 'pending'
                                        ? 'rgba(255, 152, 0, 0.2)'
                                        : 'rgba(158, 158, 158, 0.2)',
                                      color: booking.resource.status === 'confirmed'
                                        ? '#4caf50'
                                        : booking.resource.status === 'pending'
                                        ? '#ff9800'
                                        : '#9e9e9e',
                                      fontSize: { xs: '0.6875rem', md: '0.75rem' },
                                      fontWeight: 'bold',
                                      textTransform: 'capitalize',
                                      mt: 'auto',
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    {booking.resource.status}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              p: { xs: 2, md: 3 },
                              borderRadius: '50%',
                              backgroundColor: 'rgba(213, 181, 119, 0.1)',
                              mb: { xs: 2, md: 3 },
                            }}
                          >
                            <WeekendIcon sx={{ fontSize: { xs: 48, md: 64 }, color: '#D5B577', opacity: 0.8 }} />
                          </Box>
                          <Typography variant="h6" sx={{ color: '#D5B577', mb: { xs: 1, md: 1.5 }, fontWeight: 'bold', fontSize: { xs: '1.125rem', md: '1.5rem' } }}>
                            No bookings yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9CA3AF', mb: { xs: 3, md: 4 }, fontSize: { xs: '0.875rem', md: '1.05rem' } }}>
                            Start exploring properties to make your first booking!
                          </Typography>
                          <Box
                            component={Link}
                            to="/properties"
                            sx={{
                              display: 'inline-block',
                              px: { xs: 3, md: 4 },
                              py: { xs: 1.25, md: 2 },
                              backgroundColor: '#D5B577',
                              color: '#1D2A3B',
                              borderRadius: { xs: 2, md: 2.5 },
                              textDecoration: 'none',
                              fontWeight: 'bold',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              transition: 'all 0.3s ease',
                              boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                              '&:hover': {
                                backgroundColor: '#c4a366',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(213, 181, 119, 0.4)',
                              },
                            }}
                          >
                            Browse Properties
                          </Box>
                        </Box>
                      )}
                  </Box>
                </Box>
              )}

              {/* Saved Properties View */}
              {activeView === 'saved' && (
                <Box>
                  {/* Enhanced Header */}
                  <Box 
                    sx={{ 
                      mb: { xs: 2, md: 4 },
                      p: { xs: 1.5, sm: 2, md: 3 },
                      borderRadius: { xs: 2, md: 3 },
                      background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.08) 50%, rgba(213, 181, 119, 0.05) 100%)',
                      border: '1px solid rgba(213, 181, 119, 0.3)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #D5B577 0%, rgba(213, 181, 119, 0.5) 100%)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexDirection: { xs: 'row', sm: 'row' }, textAlign: 'left' }}>
                      <Box
                        sx={{
                          p: { xs: 1, md: 2 },
                          borderRadius: { xs: 1.5, md: 2 },
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.3) 0%, rgba(213, 181, 119, 0.15) 100%)',
                          color: '#D5B577',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(213, 181, 119, 0.2)',
                          flexShrink: 0,
                        }}
                      >
                        <StoreIcon sx={{ fontSize: { xs: 20, md: 32 } }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, lineHeight: 1.2 }}>
                          Saved Properties
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#9CA3AF', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, mt: { xs: 0.25, md: 0.5 }, display: { xs: 'none', sm: 'block' } }}>
                          Your favorite properties saved for later
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Card 
                    sx={{ 
                      border: '1px solid rgba(213, 181, 119, 0.3)', 
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                      borderRadius: { xs: 2, md: 3 },
                      background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.95) 100%)',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 4 } }}>
                      {savedPropertiesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, md: 6 } }}>
                          <CircularProgress sx={{ color: '#D5B577' }} />
                        </Box>
                      ) : savedPropertiesList.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              p: { xs: 2, md: 3 },
                              borderRadius: '50%',
                              backgroundColor: 'rgba(213, 181, 119, 0.1)',
                              mb: { xs: 2, md: 3 },
                            }}
                          >
                            <StoreIcon sx={{ fontSize: { xs: 56, md: 72 }, color: '#D5B577', opacity: 0.8 }} />
                          </Box>
                          <Typography variant="h5" sx={{ color: '#D5B577', mb: { xs: 1, md: 1.5 }, fontWeight: 'bold', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                            No saved properties yet
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#9CA3AF', mb: { xs: 3, md: 4 }, fontSize: { xs: '0.875rem', md: '1.05rem' } }}>
                            Save your favorite properties to view them here later
                          </Typography>
                          <Box
                            component={Link}
                            to="/properties"
                            sx={{
                              display: 'inline-block',
                              px: { xs: 3, md: 4 },
                              py: { xs: 1.25, md: 2 },
                              backgroundColor: '#D5B577',
                              color: '#1D2A3B',
                              borderRadius: { xs: 2, md: 2.5 },
                              textDecoration: 'none',
                              fontWeight: 'bold',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              transition: 'all 0.3s ease',
                              boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                              '&:hover': {
                                backgroundColor: '#c4a366',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(213, 181, 119, 0.4)',
                              },
                            }}
                          >
                            Browse Properties
                          </Box>
                        </Box>
                      ) : (
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                          {savedPropertiesList.map((property) => (
                            <Grid item xs={12} sm={6} md={4} key={property.id}>
                              <Card
                                component={Link}
                                to={`/properties/${property.id}`}
                                sx={{
                                  textDecoration: 'none',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  border: '1px solid rgba(213, 181, 119, 0.2)',
                                  borderRadius: { xs: 2, md: 2.5 },
                                  overflow: 'hidden',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.95) 100%)',
                                  '&:hover': {
                                    transform: 'translateY(-6px)',
                                    boxShadow: '0 12px 32px rgba(213, 181, 119, 0.35)',
                                    borderColor: 'rgba(213, 181, 119, 0.5)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: { xs: 180, sm: 200, md: 220 },
                                    overflow: 'hidden',
                                    position: 'relative',
                                  }}
                                >
                                  <img
                                    src={property.image || (property.images && property.images[0]) || (property.buildingImages && property.buildingImages[0]) || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80'}
                                    alt={getI18nString(property.title)}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                  {property.featured && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: { xs: 8, md: 12 },
                                        left: { xs: 8, md: 12 },
                                        px: { xs: 1, md: 1.5 },
                                        py: { xs: 0.25, md: 0.5 },
                                        borderRadius: { xs: 1.5, md: 2 },
                                        backgroundColor: '#D5B577',
                                        color: '#1D2A3B',
                                        fontSize: { xs: '0.6875rem', md: '0.75rem' },
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                      }}
                                    >
                                      FEATURED
                                    </Box>
                                  )}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      bottom: { xs: 8, md: 12 },
                                      left: { xs: 8, md: 12 },
                                      px: { xs: 1.5, md: 2 },
                                      py: { xs: 0.75, md: 1 },
                                      borderRadius: { xs: 1.5, md: 2 },
                                      backgroundColor: 'rgba(29, 42, 59, 0.95)',
                                      backdropFilter: 'blur(10px)',
                                      color: '#D5B577',
                                      fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                      fontWeight: '600',
                                      border: '1px solid rgba(213, 181, 119, 0.3)',
                                  }}
                                >
                                  {formatPrice(property.price, false)} / night
                                </Box>
                                </Box>
                                <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        color: '#D5B577',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1rem', md: '1.1rem' },
                                        lineHeight: 1.3,
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {getI18nString(property.title, 'Property')}
                                    </Typography>
                                    {(property.shortId || getI18nString(property.code)) && (
                                      <Box
                                        sx={{
                                          px: { xs: 0.75, md: 1 },
                                          py: { xs: 0.25, md: 0.5 },
                                          borderRadius: { xs: 1, md: 1.5 },
                                          backgroundColor: 'rgba(213, 181, 119, 0.2)',
                                          color: '#D5B577',
                                          fontSize: { xs: '0.625rem', md: '0.7rem' },
                                          fontWeight: 'bold',
                                          flexShrink: 0,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {property.shortId || getI18nString(property.code)}
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ 
                                      color: '#9CA3AF', 
                                      mb: { xs: 1, md: 1.5 },
                                      fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {getI18nString(property.address) || getI18nString(property.city)}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: { xs: 1, md: 1.5 }, borderTop: '1px solid rgba(213, 181, 119, 0.2)' }}>
                                    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
                                      {(property.rooms ?? property.bedrooms) != null && (
                                        <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                          {property.rooms ?? property.bedrooms} Beds
                                        </Typography>
                                      )}
                                      {(property.bathrooms) != null && (
                                        <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                          {property.bathrooms} Baths
                                        </Typography>
                                      )}
                                      {(property.size ?? property.sqft) != null && (
                                        <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                          {typeof (property.size ?? property.sqft) === 'number' ? Math.round(property.size ?? property.sqft) : property.size ?? property.sqft} m²
                                        </Typography>
                                      )}
                                    </Box>
                                    <Button
                                      size="small"
                                      onClick={(e) => handleRemoveSaved(e, property.id)}
                                      startIcon={<FavoriteIcon />}
                                      sx={{
                                        color: '#D5B577',
                                        borderColor: 'rgba(213, 181, 119, 0.5)',
                                        '&:hover': { borderColor: '#D5B577', backgroundColor: 'rgba(213, 181, 119, 0.1)' },
                                      }}
                                      variant="outlined"
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Profile View */}
              {activeView === 'profile' && (
                <Box>
                  {/* Enhanced Header */}
                  <Box 
                    sx={{ 
                      mb: { xs: 2, md: 5 },
                      p: { xs: 1.5, sm: 2, md: 4 },
                      borderRadius: { xs: 2, md: 3 },
                      background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.08) 50%, rgba(213, 181, 119, 0.05) 100%)',
                      border: '1px solid rgba(213, 181, 119, 0.3)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #D5B577 0%, rgba(213, 181, 119, 0.5) 100%)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexDirection: { xs: 'row', sm: 'row' }, textAlign: 'left' }}>
                      <Box
                        sx={{
                          p: { xs: 1, md: 2 },
                          borderRadius: { xs: 1.5, md: 2 },
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.3) 0%, rgba(213, 181, 119, 0.15) 100%)',
                          color: '#D5B577',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(213, 181, 119, 0.2)',
                          flexShrink: 0,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: { xs: 20, md: 32 } }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, lineHeight: 1.2 }}>
                          My Profile
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#9CA3AF', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, mt: { xs: 0.25, md: 0.5 }, display: { xs: 'none', sm: 'block' } }}>
                          Manage your personal information and account settings
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Card 
                    sx={{ 
                      border: '1px solid rgba(213, 181, 119, 0.3)', 
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                      borderRadius: { xs: 2, md: 3 },
                      background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.95) 100%)',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 5 } }}>
                      {/* Personal Information Section */}
                      <Box 
                        sx={{ 
                          mb: { xs: 2, md: 5 },
                          p: { xs: 1.5, sm: 2, md: 4 },
                          borderRadius: { xs: 2, md: 3 },
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.08) 0%, rgba(213, 181, 119, 0.04) 100%)',
                          border: '1px solid rgba(213, 181, 119, 0.25)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: { xs: 1, md: 2 },
                          mb: { xs: 2, md: 4 },
                        }}>
                          <Box
                            sx={{
                              p: { xs: 0.75, md: 1.5 },
                              borderRadius: { xs: 1.5, md: 2 },
                              background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                              color: '#D5B577',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <PersonIcon sx={{ fontSize: { xs: 16, md: 24 } }} />
                          </Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' } }}>
                            Personal Information
                          </Typography>
                        </Box>
                        {!isEditingProfile ? (
                            <>
                              <Grid container spacing={{ xs: 1.5, md: 3 }}>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, flex: 1 }}>
                                      {user?.name || 'Not set'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <EmailIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, wordBreak: 'break-word', flex: 1 }}>
                                      {user?.email || 'Not set'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <BadgeIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, flex: 1 }}>
                                      {user?.nationality || profileFormData.nationality || 'Egyptian'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <PhoneIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, wordBreak: 'break-word', flex: 1 }}>
                                      {user?.countryCode && user?.phone ? `${user.countryCode} ${user.phone}` : 
                                       profileFormData.countryCode && profileFormData.phone ? `${profileFormData.countryCode} ${profileFormData.phone}` :
                                       'Not set'}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 2, md: 3 } }}>
                                <Button
                                  onClick={() => setIsEditingProfile(true)}
                                  variant="text"
                                  startIcon={<EditIcon />}
                                  sx={{
                                    color: '#D5B577',
                                    fontWeight: 'bold',
                                    px: { xs: 1.5, md: 2 },
                                    py: { xs: 0.5, md: 0.75 },
                                    textTransform: 'none',
                                    fontSize: { xs: '0.8125rem', md: '0.9375rem' },
                                    '&:hover': {
                                      backgroundColor: 'rgba(213, 181, 119, 0.1)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Edit
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <form onSubmit={handleProfileInfoSubmit}>
                              <Grid container spacing={{ xs: 1.5, md: 3 }}>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, flex: 1 }}>
                                      {user?.name || 'Not set'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <EmailIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, wordBreak: 'break-word', flex: 1 }}>
                                      {user?.email || 'Not set'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box 
                                    sx={{ 
                                      p: { xs: 1.5, md: 3 },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1.5, md: 2 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: { xs: 0.75, md: 1.5 },
                                        borderRadius: { xs: 1, md: 1.5 },
                                        backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                        color: '#D5B577',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <BadgeIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: '600', fontSize: { xs: '0.9375rem', md: '1.1rem' }, lineHeight: 1.3, flex: 1 }}>
                                      {user?.nationality || profileFormData.nationality || 'Egyptian'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 1.5, md: 2 } }}>
                                      <Box
                                        sx={{
                                          p: { xs: 0.75, md: 1.5 },
                                          borderRadius: { xs: 1, md: 1.5 },
                                          backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                          color: '#D5B577',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <PhoneIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                                      </Box>
                                      <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: '600', fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                                        Phone Number
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 1.5 } }}>
                                      <TextField
                                        select
                                        label="Country Code"
                                        name="countryCode"
                                        value={profileFormData.countryCode}
                                        onChange={handleProfileChange}
                                        SelectProps={{
                                          native: true,
                                        }}
                                        size="small"
                                        sx={{
                                          width: { xs: '100%', sm: '140px' },
                                          flexShrink: { xs: 0, sm: 0 },
                                          '& .MuiOutlinedInput-root': {
                                            color: '#ffffff',
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& fieldset': {
                                              borderColor: 'rgba(213, 181, 119, 0.3)',
                                            },
                                            '&:hover fieldset': {
                                              borderColor: 'rgba(213, 181, 119, 0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: '#D5B577',
                                              boxShadow: '0 0 0 3px rgba(213, 181, 119, 0.1)',
                                            },
                                          },
                                          '& .MuiInputLabel-root': {
                                            color: '#9CA3AF',
                                            '&.Mui-focused': {
                                              color: '#D5B577',
                                            },
                                          },
                                        }}
                                      >
                                        <option value="+20">🇪🇬 +20</option>
                                        <option value="+966">🇸🇦 +966</option>
                                        <option value="+971">🇦🇪 +971</option>
                                        <option value="+965">🇰🇼 +965</option>
                                        <option value="+974">🇶🇦 +974</option>
                                        <option value="+968">🇴🇲 +968</option>
                                        <option value="+973">🇧🇭 +973</option>
                                        <option value="+962">🇯🇴 +962</option>
                                        <option value="+961">🇱🇧 +961</option>
                                        <option value="+963">🇸🇾 +963</option>
                                        <option value="+964">🇮🇶 +964</option>
                                        <option value="+970">🇵🇸 +970</option>
                                        <option value="+967">🇾🇪 +967</option>
                                        <option value="+249">🇸🇩 +249</option>
                                        <option value="+218">🇱🇾 +218</option>
                                        <option value="+216">🇹🇳 +216</option>
                                        <option value="+213">🇩🇿 +213</option>
                                        <option value="+212">🇲🇦 +212</option>
                                        <option value="+1">🇺🇸 +1</option>
                                        <option value="+44">🇬🇧 +44</option>
                                        <option value="+33">🇫🇷 +33</option>
                                        <option value="+49">🇩🇪 +49</option>
                                        <option value="+39">🇮🇹 +39</option>
                                        <option value="+34">🇪🇸 +34</option>
                                        <option value="+61">🇦🇺 +61</option>
                                      </TextField>
                                      <TextField
                                        fullWidth
                                        type="tel"
                                        label="Phone Number"
                                        name="phone"
                                        value={profileFormData.phone}
                                        onChange={handleProfileChange}
                                        placeholder="Enter your phone number"
                                        size="small"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            color: '#ffffff',
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            '& fieldset': {
                                              borderColor: 'rgba(213, 181, 119, 0.3)',
                                            },
                                            '&:hover fieldset': {
                                              borderColor: 'rgba(213, 181, 119, 0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: '#D5B577',
                                              boxShadow: '0 0 0 3px rgba(213, 181, 119, 0.1)',
                                            },
                                          },
                                          '& .MuiInputLabel-root': {
                                            color: '#9CA3AF',
                                            '&.Mui-focused': {
                                              color: '#D5B577',
                                            },
                                          },
                                          '& .MuiInputBase-input::placeholder': {
                                            color: 'rgba(156, 163, 175, 0.5)',
                                            opacity: 1,
                                          },
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                              
                              {/* Success Message */}
                              {profileSuccess && (
                                <Box
                                  sx={{
                                    p: { xs: 1.5, md: 2.5 },
                                    mt: { xs: 2, md: 4 },
                                    mb: { xs: 1.5, md: 2 },
                                    borderRadius: { xs: 1.5, md: 2 },
                                    backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                    border: '1px solid rgba(34, 197, 94, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 1.5 },
                                  }}
                                >
                                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: '500', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                    Profile updated successfully!
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Error Message */}
                              {profileErrors.submit && (
                                <Box
                                  sx={{
                                    p: { xs: 1.5, md: 2.5 },
                                    mt: { xs: 2, md: 4 },
                                    mb: { xs: 1.5, md: 2 },
                                    borderRadius: { xs: 1.5, md: 2 },
                                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 1.5 },
                                  }}
                                >
                                  <CancelIcon sx={{ color: '#ef4444', fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: '500', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                    {profileErrors.submit}
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, md: 2 }, mt: { xs: 2, md: 4 }, width: '100%' }}>
                                <Button
                                  type="submit"
                                  variant="contained"
                                  disabled={profileLoading}
                                  sx={{
                                    backgroundColor: '#D5B577',
                                    color: '#1D2A3B',
                                    fontWeight: 'bold',
                                    px: { xs: 2.5, md: 5 },
                                    py: { xs: 1, md: 1.75 },
                                    borderRadius: { xs: 1.5, md: 2 },
                                    textTransform: 'none',
                                    fontSize: { xs: '0.8125rem', md: '1rem' },
                                    width: { xs: '100%', sm: 'auto' },
                                    boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      backgroundColor: '#c4a366',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 6px 16px rgba(213, 181, 119, 0.4)',
                                    },
                                    '&:disabled': {
                                      backgroundColor: 'rgba(213, 181, 119, 0.5)',
                                      color: 'rgba(29, 42, 59, 0.5)',
                                    },
                                  }}
                                >
                                  {profileLoading ? 'Saving...' : 'Save Changes'}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outlined"
                                  onClick={() => {
                                    setIsEditingProfile(false);
                                    setProfileFormData(prev => ({
                                      ...prev,
                                      phone: user?.phone || '',
                                      countryCode: user?.countryCode || '+20',
                                    }));
                                    setProfileErrors({});
                                    setProfileSuccess(false);
                                  }}
                                  sx={{
                                    borderColor: 'rgba(213, 181, 119, 0.5)',
                                    color: '#D5B577',
                                    fontWeight: 'bold',
                                    px: { xs: 2.5, md: 5 },
                                    py: { xs: 1, md: 1.75 },
                                    borderRadius: { xs: 1.5, md: 2 },
                                    textTransform: 'none',
                                    fontSize: { xs: '0.8125rem', md: '1rem' },
                                    width: { xs: '100%', sm: 'auto' },
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      borderColor: '#D5B577',
                                      backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </form>
                          )}
                        </Box>
                        
                        {/* ID Verification Section */}
                        <Box 
                          sx={{ 
                            mb: { xs: 3, md: 5 },
                            p: { xs: 2, sm: 2.5, md: 4 },
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.08) 0%, rgba(213, 181, 119, 0.04) 100%)',
                            border: '1px solid rgba(213, 181, 119, 0.25)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 3, md: 4 } }}>
                            <Box
                              sx={{
                                p: { xs: 1, md: 1.5 },
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                                color: '#D5B577',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <VerifiedUserIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                              ID Verification
                            </Typography>
                          </Box>
                          
                          {/* Status Display */}
                          <Box 
                            sx={{ 
                              mb: { xs: 3, md: 4 },
                              p: { xs: 2, md: 3 },
                              borderRadius: 2.5,
                              background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.08) 0%, rgba(213, 181, 119, 0.04) 100%)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 1.5, md: 2 }, flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ color: '#9CA3AF', fontWeight: '600', fontSize: { xs: '0.875rem', md: '0.95rem' } }}>
                                Verification Status:
                              </Typography>
                              <Box
                                sx={{
                                  px: 2.5,
                                  py: 1,
                                  borderRadius: 2,
                                  backgroundColor: 
                                    idUploadStatus === 'pending' ? 'rgba(255, 152, 0, 0.2)' :
                                    idUploadStatus === 'uploaded' ? 'rgba(33, 150, 243, 0.2)' :
                                    idUploadStatus === 'approved' ? 'rgba(76, 175, 80, 0.2)' :
                                    'rgba(239, 68, 68, 0.2)',
                                  color: 
                                    idUploadStatus === 'pending' ? '#ff9800' :
                                    idUploadStatus === 'uploaded' ? '#2196f3' :
                                    idUploadStatus === 'approved' ? '#4caf50' :
                                    '#ef4444',
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                  textTransform: 'capitalize',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {idUploadStatus === 'approved' && <CheckCircleIcon sx={{ fontSize: 18 }} />}
                                {idUploadStatus === 'pending' ? 'Pending Upload' :
                                 idUploadStatus === 'uploaded' ? 'Under Review' :
                                 idUploadStatus === 'approved' ? 'Approved' :
                                 'Rejected'}
                              </Box>
                            </Box>
                            {idUploadStatus === 'pending' && (
                              <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.6, fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                                Please upload a valid ID document (National ID, Passport, or Driver's License) to verify your identity. This helps us ensure account security.
                              </Typography>
                            )}
                            {idUploadStatus === 'uploaded' && (
                              <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.6, fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                                Your ID document has been uploaded successfully and is currently under review. We'll notify you once the verification is complete.
                              </Typography>
                            )}
                            {idUploadStatus === 'approved' && (
                              <Typography variant="body2" sx={{ color: '#4caf50', lineHeight: 1.6, fontWeight: '500', fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                                Your identity has been verified successfully. Your account is now fully verified.
                              </Typography>
                            )}
                          </Box>
                          
                          {/* File Upload Area */}
                          <Box
                            sx={{
                              border: '2px dashed rgba(213, 181, 119, 0.4)',
                              borderRadius: 3,
                              p: { xs: 2, sm: 2.5, md: 5 },
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                borderColor: 'rgba(213, 181, 119, 0.6)',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                boxShadow: '0 4px 16px rgba(213, 181, 119, 0.15)',
                              },
                            }}
                          >
                            {idDocumentPreview ? (
                              <Box>
                                <Box 
                                  sx={{ 
                                    mb: { xs: 2, md: 3 },
                                    p: { xs: 2, md: 3 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.1) 0%, rgba(213, 181, 119, 0.05) 100%)',
                                    border: '1px solid rgba(213, 181, 119, 0.3)',
                                  }}
                                >
                                  <DescriptionIcon sx={{ fontSize: { xs: 40, md: 56 }, color: '#D5B577', mb: { xs: 1.5, md: 2 }, opacity: 0.9 }} />
                                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 1, fontWeight: '600', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                    {idDocument.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                                    File size: {(idDocument.size / 1024 / 1024).toFixed(2)} MB
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, md: 2 }, justifyContent: 'center', width: '100%' }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={() => {
                                      setIdDocument(null);
                                      setIdDocumentPreview(null);
                                    }}
                                    sx={{
                                      borderColor: 'rgba(239, 68, 68, 0.5)',
                                      color: '#ef4444',
                                      fontWeight: 'bold',
                                      px: { xs: 3, md: 4 },
                                      py: { xs: 1.25, md: 1.5 },
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontSize: { xs: '0.875rem', md: '1rem' },
                                      width: { xs: '100%', sm: 'auto' },
                                      '&:hover': {
                                        borderColor: '#ef4444',
                                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                        transform: 'translateY(-2px)',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                  >
                                    Remove
                                  </Button>
                                  <Button
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={async () => {
                                      if (!idDocument) return;
                                      setIdUploading(true);
                                      try {
                                        // Simulate upload
                                        await new Promise(resolve => setTimeout(resolve, 1500));
                                        setIdUploadStatus('uploaded');
                                        setIdUploading(false);
                                      } catch (error) {
                                        console.error('Upload error:', error);
                                        setIdUploading(false);
                                      }
                                    }}
                                    disabled={idUploading}
                                    sx={{
                                      backgroundColor: '#D5B577',
                                      color: '#1D2A3B',
                                      fontWeight: 'bold',
                                      px: { xs: 3, md: 4 },
                                      py: { xs: 1.25, md: 1.5 },
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontSize: { xs: '0.875rem', md: '1rem' },
                                      width: { xs: '100%', sm: 'auto' },
                                      boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        backgroundColor: '#c4a366',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 16px rgba(213, 181, 119, 0.4)',
                                      },
                                      '&:disabled': {
                                        backgroundColor: 'rgba(213, 181, 119, 0.5)',
                                        color: 'rgba(29, 42, 59, 0.5)',
                                      },
                                    }}
                                  >
                                    {idUploading ? 'Uploading...' : 'Upload Document'}
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box>
                                <input
                                  accept="image/*,.pdf"
                                  style={{ display: 'none' }}
                                  id="id-document-upload"
                                  type="file"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      setIdDocument(file);
                                      // Create preview for images
                                      if (file.type.startsWith('image/')) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          setIdDocumentPreview(reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                      } else {
                                        setIdDocumentPreview('pdf');
                                      }
                                    }
                                  }}
                                />
                                <label htmlFor="id-document-upload">
                                  <Box
                                    sx={{
                                      cursor: 'pointer',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: 2,
                                    }}
                                  >
                                    <CloudUploadIcon sx={{ fontSize: { xs: 48, md: 72 }, color: '#D5B577', opacity: 0.8, mb: 1 }} />
                                    <Box>
                                      <Typography variant="h5" sx={{ color: '#D5B577', mb: { xs: 1, md: 1.5 }, fontWeight: 'bold', fontSize: { xs: '1.125rem', md: '1.5rem' } }}>
                                        Upload ID Document
                                      </Typography>
                                      <Typography variant="body1" sx={{ color: '#9CA3AF', mb: { xs: 1, md: 1.5 }, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                        Click here or drag and drop to upload your document
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                        Supported formats: JPG, PNG, PDF • Maximum file size: 10MB
                                      </Typography>
                                    </Box>
                                    <Button
                                      component="span"
                                      variant="outlined"
                                      sx={{
                                        borderColor: 'rgba(213, 181, 119, 0.5)',
                                        color: '#D5B577',
                                        fontWeight: 'bold',
                                        px: { xs: 3, md: 5 },
                                        py: { xs: 1.25, md: 1.75 },
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        mt: { xs: 1, md: 2 },
                                        width: { xs: '100%', sm: 'auto' },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          borderColor: '#D5B577',
                                          backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                          transform: 'translateY(-2px)',
                                          boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                                        },
                                      }}
                                    >
                                      Choose File
                                    </Button>
                                  </Box>
                                </label>
                              </Box>
                            )}
                          </Box>
                          
                          {idDocumentPreview && idDocumentPreview !== 'pdf' && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <img
                                src={idDocumentPreview}
                                alt="ID Preview"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '300px',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(213, 181, 119, 0.3)',
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        
                        {/* Account Settings Section */}
                        <Box 
                          sx={{ 
                            p: { xs: 2, sm: 2.5, md: 4 },
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.08) 0%, rgba(213, 181, 119, 0.04) 100%)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 3, md: 4 } }}>
                            <Box
                              sx={{
                                p: { xs: 1, md: 1.5 },
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                                color: '#D5B577',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <SecurityIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                              Account Settings
                            </Typography>
                          </Box>
                          
                          {/* Change Password Button */}
                          <Button
                            onClick={handleTogglePasswordForm}
                            variant="outlined"
                            startIcon={showPasswordForm ? <CancelIcon /> : <LockIcon />}
                            sx={{
                              borderColor: 'rgba(213, 181, 119, 0.5)',
                              color: '#D5B577',
                              fontWeight: 'bold',
                              px: { xs: 3, md: 4 },
                              py: { xs: 1.25, md: 1.75 },
                              mb: { xs: 3, md: 4 },
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              width: { xs: '100%', sm: 'auto' },
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#D5B577',
                                backgroundColor: 'rgba(213, 181, 119, 0.15)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                              },
                            }}
                          >
                            {showPasswordForm ? 'Cancel Password Change' : 'Change Password'}
                          </Button>
                          
                          {/* Password Change Form - Shown when button is clicked */}
                          {showPasswordForm && (
                            <Box
                              sx={{
                                mt: { xs: 2, md: 3 },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, mb: { xs: 2.5, md: 3.5 } }}>
                                <Box
                                  sx={{
                                    p: { xs: 0.75, md: 1 },
                                    borderRadius: { xs: 1, md: 1.5 },
                                    background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)',
                                    color: '#D5B577',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <LockIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#D5B577', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                                  Update Your Password
                                </Typography>
                              </Box>
                              <form onSubmit={handleProfileSubmit}>
                                {/* Current Password */}
                                <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                                  <TextField
                                    fullWidth
                                    type="password"
                                    label="Current Password"
                                    name="currentPassword"
                                    value={profileFormData.currentPassword}
                                    onChange={handleProfileChange}
                                    error={!!profileErrors.currentPassword}
                                    helperText={profileErrors.currentPassword}
                                    placeholder="Enter your current password"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                        borderRadius: { xs: 1.5, md: 2 },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        minHeight: { xs: '40px', md: '56px' },
                                        height: { xs: 'auto', md: 'auto' },
                                        '& fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.3)',
                                          borderWidth: '1.5px',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.6)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#D5B577',
                                          borderWidth: '2px',
                                          boxShadow: '0 0 0 4px rgba(213, 181, 119, 0.15)',
                                        },
                                        '&.Mui-error fieldset': {
                                          borderColor: '#ef4444',
                                        },
                                      },
                                      '& .MuiInputLabel-root': {
                                        color: '#9CA3AF',
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&.Mui-focused': {
                                          color: '#D5B577',
                                        },
                                        '&.Mui-error': {
                                          color: '#ef4444',
                                        },
                                      },
                                      '& .MuiFormHelperText-root': {
                                        color: '#ef4444',
                                        fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                        mt: { xs: 0.75, md: 1 },
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: { xs: '10px 12px', md: '14px 16px' },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&::placeholder': {
                                          color: 'rgba(156, 163, 175, 0.6)',
                                          opacity: 1,
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                        },
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& .MuiOutlinedInput-input': {
                                          padding: { xs: '10px 12px', md: '14px 16px' },
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                                
                                {/* New Password */}
                                <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                                  <TextField
                                    fullWidth
                                    type="password"
                                    label="New Password"
                                    name="newPassword"
                                    value={profileFormData.newPassword}
                                    onChange={handleProfileChange}
                                    error={!!profileErrors.newPassword}
                                    helperText={profileErrors.newPassword || 'Must be at least 6 characters'}
                                    placeholder="Enter your new password"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                        borderRadius: { xs: 1.5, md: 2 },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        minHeight: { xs: '40px', md: '56px' },
                                        height: { xs: 'auto', md: 'auto' },
                                        '& fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.3)',
                                          borderWidth: '1.5px',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.6)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#D5B577',
                                          borderWidth: '2px',
                                          boxShadow: '0 0 0 4px rgba(213, 181, 119, 0.15)',
                                        },
                                        '&.Mui-error fieldset': {
                                          borderColor: '#ef4444',
                                        },
                                      },
                                      '& .MuiInputLabel-root': {
                                        color: '#9CA3AF',
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&.Mui-focused': {
                                          color: '#D5B577',
                                        },
                                        '&.Mui-error': {
                                          color: '#ef4444',
                                        },
                                      },
                                      '& .MuiFormHelperText-root': {
                                        color: profileErrors.newPassword ? '#ef4444' : '#9CA3AF',
                                        fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                        mt: { xs: 0.75, md: 1 },
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: { xs: '10px 12px', md: '14px 16px' },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&::placeholder': {
                                          color: 'rgba(156, 163, 175, 0.6)',
                                          opacity: 1,
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                        },
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& .MuiOutlinedInput-input': {
                                          padding: { xs: '10px 12px', md: '14px 16px' },
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                                
                                {/* Confirm Password */}
                                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                  <TextField
                                    fullWidth
                                    type="password"
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    value={profileFormData.confirmPassword}
                                    onChange={handleProfileChange}
                                    error={!!profileErrors.confirmPassword}
                                    helperText={profileErrors.confirmPassword}
                                    placeholder="Confirm your new password"
                                    size={window.innerWidth < 600 ? 'small' : 'medium'}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                        borderRadius: { xs: 1.5, md: 2 },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        minHeight: { xs: '40px', md: '56px' },
                                        height: { xs: 'auto', md: 'auto' },
                                        '& fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.3)',
                                          borderWidth: '1.5px',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'rgba(213, 181, 119, 0.6)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#D5B577',
                                          borderWidth: '2px',
                                          boxShadow: '0 0 0 4px rgba(213, 181, 119, 0.15)',
                                        },
                                        '&.Mui-error fieldset': {
                                          borderColor: '#ef4444',
                                        },
                                      },
                                      '& .MuiInputLabel-root': {
                                        color: '#9CA3AF',
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&.Mui-focused': {
                                          color: '#D5B577',
                                        },
                                        '&.Mui-error': {
                                          color: '#ef4444',
                                        },
                                      },
                                      '& .MuiFormHelperText-root': {
                                        color: '#ef4444',
                                        fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                        mt: { xs: 0.75, md: 1 },
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: { xs: '10px 12px', md: '14px 16px' },
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        '&::placeholder': {
                                          color: 'rgba(156, 163, 175, 0.6)',
                                          opacity: 1,
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                        },
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& .MuiOutlinedInput-input': {
                                          padding: { xs: '10px 12px', md: '14px 16px' },
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                              
                                {/* Success Message */}
                                {profileSuccess && (
                                  <Box
                                    sx={{
                                      p: { xs: 1.5, md: 2.5 },
                                      mb: { xs: 2, md: 3 },
                                      borderRadius: { xs: 1.5, md: 2 },
                                      backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                      border: '1px solid rgba(34, 197, 94, 0.4)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1, md: 1.5 },
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: '500', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                      Password updated successfully!
                                    </Typography>
                                  </Box>
                                )}
                                
                                {/* Error Message */}
                                {profileErrors.submit && (
                                  <Box
                                    sx={{
                                      p: { xs: 1.5, md: 2.5 },
                                      mb: { xs: 2, md: 3 },
                                      borderRadius: { xs: 1.5, md: 2 },
                                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                      border: '1px solid rgba(239, 68, 68, 0.4)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1, md: 1.5 },
                                    }}
                                  >
                                    <CancelIcon sx={{ color: '#ef4444', fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: '500', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                      {profileErrors.submit}
                                    </Typography>
                                  </Box>
                                )}
                                
                                {/* Submit Button */}
                                <Button
                                  type="submit"
                                  variant="contained"
                                  disabled={profileLoading}
                                  startIcon={<LockIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
                                  sx={{
                                    backgroundColor: '#D5B577',
                                    color: '#1D2A3B',
                                    fontWeight: 'bold',
                                    px: { xs: 3, md: 5 },
                                    py: { xs: 1.25, md: 1.75 },
                                    borderRadius: { xs: 1.5, md: 2 },
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    width: { xs: '100%', sm: 'auto' },
                                    minWidth: { xs: 'auto', sm: '160px' },
                                    boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      backgroundColor: '#c4a366',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 6px 16px rgba(213, 181, 119, 0.4)',
                                    },
                                    '&:disabled': {
                                      backgroundColor: 'rgba(213, 181, 119, 0.5)',
                                      color: 'rgba(29, 42, 59, 0.5)',
                                    },
                                  }}
                                >
                                  {profileLoading ? 'Updating...' : 'Update Password'}
                                </Button>
                              </form>
                            </Box>
                          )}
                        </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Settings View */}
              {activeView === 'settings' && (
                <Box>
                  <Box 
                    sx={{ 
                      mb: 4,
                      p: 3,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.1) 0%, rgba(213, 181, 119, 0.05) 100%)',
                      border: '1px solid rgba(213, 181, 119, 0.2)',
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D5B577', mb: 1 }}>
                      Settings
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#9CA3AF' }}>
                      Manage your account preferences and settings
                    </Typography>
                  </Box>
                  <Card 
                    sx={{ 
                      border: '1px solid rgba(213, 181, 119, 0.3)', 
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      borderRadius: 3,
                    }}
              >
                    <CardContent>
                      <Box sx={{ p: 4 }}>
                        <Box
                          sx={{
                            mb: 4,
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(213, 181, 119, 0.05)',
                            border: '1px solid rgba(213, 181, 119, 0.2)',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#D5B577', mb: 2 }}>
                            Preferences
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 3 }}>
                            Account preferences and notification settings will be available here
                          </Typography>
                        </Box>
                        <Box
                          component="button"
                          onClick={handleLogout}
                          sx={{
                            px: 4,
                            py: 2,
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: 2.5,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.2)',
                              borderColor: 'rgba(239, 68, 68, 0.5)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
                            },
                          }}
              >
                          Logout
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Footer />
      </div>
    </ThemeProvider>
  );
};

export default ClientDashboard;
